import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { TextField, SelectField, AreaField, readForm } from "@/components/site/FormFields";
import { submitInquiry } from "@/lib/submit-inquiry";

const URL = "https://avoluscars.com/share-review";
const TITLE = "Share Your Review | Avolus Cars";
const DESC = "Rented with Avolus Cars? Tell us about your experience. Every review is checked before it appears on our site.";

export const Route = createFileRoute("/share-review")({
  component: ShareReviewPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
});

function ShareReviewPage() {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const get = readForm(e.currentTarget);
    const name = get("name");
    const email = get("email");
    const review = get("review");
    if (!name || !email || review.length < 10) {
      toast.error("Please add your name, email and a few words about your rental.");
      return;
    }
    setSubmitting(true);
    const message = [
      `Rating: ${get("rating")}`,
      `Vehicle: ${get("vehicle") || "Not given"}`,
      "",
      review,
    ].join("\n");
    const { error } = await submitInquiry({
      name,
      email,
      message: message.slice(0, 2000),
      formType: "review",
      subject: `New Review Submission from ${name}`.slice(0, 200),
    });
    setSubmitting(false);
    if (error) {
      toast.error("Something went wrong. Please try again or call us.");
      return;
    }
    setDone(true);
  }

  return (
    <Layout>
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Reviews</Eyebrow>
            <h1 className="font-display text-4xl md:text-6xl text-foreground mb-4">Share your review.</h1>
            <p className="text-foreground/80 leading-relaxed mb-10">
              Rented with us? We'd love to hear how it went. Every review is read by our team
              before it appears on the site, and we only show your name as you enter it.
            </p>
          </Reveal>
          {done ? (
            <div role="status" className="border border-border p-12 text-center bg-card">
              <h2 className="font-display text-3xl text-foreground mb-3">Thank you.</h2>
              <p className="text-foreground/70">Your review has been received and will be checked by our team.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="grid gap-6 md:grid-cols-2 border border-border p-8 md:p-10 bg-card">
              <TextField id="name" label="Name (as shown)" required maxLength={100} />
              <TextField id="email" label="Email (not shown)" type="email" required maxLength={255} />
              <SelectField id="rating" label="Rating" options={["5 stars", "4 stars", "3 stars", "2 stars", "1 star"]} />
              <SelectField
                id="vehicle"
                label="Vehicle"
                options={["Range Rover", "Escalade", "BMW X7", "Mercedes S580", "Porsche Macan", "BMW M3", "BMW M440i", "BMW X1", "Other"]}
              />
              <AreaField id="review" label="Your review *" maxLength={1500} />
              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Submit Review"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </Layout>
  );
}
